import { Box, Container, Typography, Grid, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import interiorIcon from "../../Assets/Icons/interiordesignIcon.svg";
import infraIcon from "../../Assets/Icons/infrastructureIcon.svg";
import realEstateIcon from "../../Assets/Icons/realestate.svg";
import bg1 from "../../Assets/BackgroundImages/Bg1.png";
import bg2 from "../../Assets/BackgroundImages/Bg2.png";
import bg3 from "../../Assets/BackgroundImages/Bg3.png";
import useResponsive from "../../Hooks/useResponsive";

const ExpertiseSection = () => {
  const { isExtraLarge } = useResponsive();
  const navigate = useNavigate();

  const handleCardClick = (path) => {
    if (path) {
      if (path.startsWith("http")) {
        window.open(path, "_blank");
      } else {
        navigate(path);
      }
    }
  };

  const expertiseData = [
    {
      title: "Interior Design",
      description:
        "Bespoke residential and commercial interiors that define luxury and functionality. From mood boards to final fit-outs.",
      link: "View Showcase",
      icon: interiorIcon,
      bgColor: "#B02F001A",
      bgImage: bg1,
      navPath: "/interior-design",
    },
    {
      title: "Infrastructure & Construction",
      description:
        "Large-scale civil projects and structural development built on the foundation of reliability and modern engineering.",
      link: "Our Projects",
      icon: infraIcon,
      bgColor: "#655D571A",
      bgImage: bg2,
      navPath: "/projects",
    },
    {
      title: "Real Estate",
      description:
        "Strategic property acquisition, sale, and resale of luxury flats. We find the space where your life happens.",
      link: "Browse Listings",
      icon: realEstateIcon,
      bgColor: "#655D571A",
      bgImage: bg3,
      navPath: "/realestate",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  const slideUpVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FCF9F8",
        pt: { xs: "80px", md: "120px" },
        pb: { xs: "80px", md: "120px" },
        minHeight: { xs: "auto", md: "600px" },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: "100%", lg: "1280px", xl: "1400px" },
          px: { xs: "24px", sm: "32px" },
          mx: "auto",
        }}
      >
        {/* Header Section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            sx={{ mb: { xs: "64px", md: "80px" }, gap: "32px" }}
          >
            <motion.div variants={slideUpVariants}>
              <Box sx={{ maxWidth: { xs: "100%", md: "373px" } }}>
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "16px",
                    letterSpacing: "1.2px",
                    color: "#B02F00",
                    textTransform: "uppercase",
                    mb: "16px",
                    textAlign: "left",
                  }}
                >
                  OUR EXPERTISE
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 800,
                    fontSize: { xs: "32px", sm: "36px", md: "48px" },
                    lineHeight: { xs: "40px", sm: "44px", md: "48px" },
                    letterSpacing: { xs: "-1.6px", md: "-2.4px" },
                    color: "#1C1917",
                    textAlign: "left",
                  }}
                >
                  Precision in Every Structural Detail.
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={slideUpVariants}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: { xs: "15px", sm: "16px" },
                  lineHeight: { xs: "24px", sm: "26px" },
                  color: "#57534E",
                  maxWidth: { xs: "100%", md: "448px" },
                  textAlign: "left",
                }}
              ></Typography>
            </motion.div>
          </Stack>
        </motion.div>

        {/* Cards Section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <Grid container spacing={4} rowSpacing={{ xs: 8, md: 4 }}>
            {expertiseData.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  variants={cardVariants}
                  whileHover={{
                    y: -8,
                    transition: { duration: 0.3, ease: "easeOut" },
                  }}
                  style={{ height: "100%" }}
                >
                  <Box
                    onClick={() => handleCardClick(item.navPath)}
                    sx={{
                      position: "relative",
                      backgroundColor: "#FFFFFF",
                      borderRadius: "16px",
                      p: { xs: "32px", sm: "40px" },
                      height: "100%",
                      minHeight: { xs: "340px", md: "400px" },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      textAlign: "left",
                      gap: "20px",
                      cursor: "pointer",
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.04)",
                      transition: "all 0.3s ease",
                      overflow: "hidden",
                      "&:hover": {
                        boxShadow: "0px 16px 40px rgba(176, 47, 0, 0.12)",
                        transform: "translateY(-4px)",
                        "& .card-bg": {
                          transform: "scale(1.1)",
                        },
                      },
                    }}
                  >
                    {/* Background Image Overlay */}
                    <Box
                      className="card-bg"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.6) 45%, rgba(255, 255, 255, 0.1) 100%), url(${item.bgImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        zIndex: 0,
                        transition: "all 0.5s ease",
                      }}
                    />

                    {/* Icon Box */}
                    <Box
                      sx={{
                        width: "56px",
                        height: "56px",
                        backgroundColor: item.bgColor,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "transform 0.3s ease",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.icon}
                        alt={item.title}
                        sx={{
                          width: "28px",
                          height: "28px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>

                    {/* Content */}
                    <Box sx={{ position: "relative", zIndex: 1, flexGrow: 1 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: "Manrope",
                          fontWeight: 700,
                          fontSize: { xs: "20px", md: "24px" },
                          color: "#1C1917",
                          mb: "12px",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 500,
                          fontSize: { xs: "14px", md: "15px" },
                          lineHeight: "1.6",
                          color: "#1C1917",
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>

                    {/* Link */}
                    <Box
                      sx={{
                        mt: "24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        color: index === 0 ? "#B02F00" : "#1C1917",
                        transition: "all 0.3s ease",
                        position: "relative",
                        zIndex: 1,
                        "&:hover": {
                          color: "#B02F00",
                          gap: "12px",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 600,
                          fontSize: "14px",
                          lineHeight: 1,
                        }}
                      >
                        {item.link}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: "18px" }} />
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ExpertiseSection;
